"use client";
import React, { Suspense } from "react";
import Link from "next/link";
import { Caveat, Inter } from "next/font/google";
import { useSearchParams } from "next/navigation";
import Image from "next/image";
import { industryData } from "../components/odooIndustriesData";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });
const inter = Inter({ subsets: ["latin"], display: "swap" });

// Floating Input Component
const FloatingInput = ({ type, id, label, required = true, defaultValue = "", name }) => (
  <div className="relative mb-6">
    <input
      type={type}
      id={id}
      name={name || id}
      required={required}
      defaultValue={defaultValue}
      className="peer block w-full rounded-lg border border-neutral-700 bg-[#0a0a0a] px-4 py-3 text-white focus:border-[#f1833b] focus:outline-none focus:ring-1 focus:ring-[#f1833b] transition-colors shadow-sm"
      placeholder=" "
    />
    <label
      htmlFor={id}
      className="absolute left-3 top-3 z-10 origin-[0] -translate-y-5 scale-75 transform text-gray-400 duration-150 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-5 peer-focus:scale-75 peer-focus:text-[#f1833b] pointer-events-none px-1"
    >
      {label}
    </label>
  </div>
);

// Floating Select Component (Always shows label at the top since select is never empty)
const FloatingSelect = ({ id, label, options, required = true, name, defaultValue }) => (
  <div className="relative mb-6">
    <select
      id={id}
      name={name || id}
      required={required}
      defaultValue={defaultValue}
      className="peer block w-full appearance-none rounded-lg border border-neutral-700 bg-[#0a0a0a] px-4 py-3 text-white focus:border-[#f1833b] focus:outline-none focus:ring-1 focus:ring-[#f1833b] transition-colors shadow-sm"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value} className="bg-gray-800 text-white">
          {opt.label}
        </option>
      ))}
    </select>
    <label
      htmlFor={id}
      className="absolute left-3 top-3 z-10 origin-[0] -translate-y-5 scale-75 transform text-gray-400 px-1 peer-focus:text-[#f1833b] pointer-events-none transition-colors"
    >
      {label}
    </label>
    <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-gray-400">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
    </div>
  </div>
);

export function StartForm({ defaultIndustry, slug }) {
  const searchParams = useSearchParams();
  const queryIndustry = searchParams.get("industry");

  // Resolve industry from slug if provided
  let resolvedIndustry = defaultIndustry;
  if (!resolvedIndustry && slug) {
    const allItems = industryData.flatMap((cat) => cat.items);
    const cleanSlug = decodeURIComponent(slug).toLowerCase();
    const itemMatch = allItems.find(
      (item) =>
        item.id.toLowerCase() === cleanSlug ||
        item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") === cleanSlug ||
        item.title.toLowerCase().replace(/\s+/g, "-") === cleanSlug
    );
    if (itemMatch) {
      resolvedIndustry = itemMatch.title;
    } else {
      resolvedIndustry = slug
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    }
  }

  const industry = resolvedIndustry || queryIndustry || "Accounting Firm";
  const [submitted, setSubmitted] = React.useState(false);

  const matchedItem = industryData
    .flatMap((cat) => cat.items)
    .find((item) => {
      const itemTitle = item.title.toLowerCase();
      const queryTitle = industry.toLowerCase();
      const slugKey = slug ? slug.toLowerCase() : "";
      return (
        item.id.toLowerCase() === slugKey ||
        itemTitle === queryTitle ||
        queryTitle.includes(itemTitle) ||
        itemTitle.includes(queryTitle.replace(/s$/, ""))
      );
    });

  const [imgSrc, setImgSrc] = React.useState(
    matchedItem && matchedItem.icon ? `/odoo/icons/industries/${matchedItem.icon}.png` : ""
  );

  React.useEffect(() => {
    if (matchedItem && matchedItem.icon) {
      setImgSrc(`/odoo/icons/industries/${matchedItem.icon}.png`);
    }
  }, [matchedItem]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const getSelectText = (id) => {
      const el = e.currentTarget.querySelector(`#${id}`);
      return el ? el.options[el.selectedIndex]?.text : "";
    };

    const data = {
      fullName: formData.get("username"),
      companyName: formData.get("company_name"),
      email: formData.get("email"),
      phone: formData.get("phone"),
      country: getSelectText("country_id"),
      language: getSelectText("lang"),
      companySize: getSelectText("company_size"),
      plan: getSelectText("plan"),
      industry: industry,
      loadDemoData: formData.get("industry_demo") === "on"
    };

    try {
      const apiUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "http://localhost:5002";
      const res = await fetch(`${apiUrl}/api/odoo-leads/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(data)
      });
      const resData = await res.json();
      if (res.ok && resData.success) {
        setSubmitted(true);
      } else {
        alert(resData.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("Error submitting Odoo lead:", err);
      alert("Failed to connect to the server. Please try again later.");
    }
  };

  const getIndustryIcon = () => {
    if (matchedItem && matchedItem.icon) {
      return (
        <div className="w-10 h-10 bg-white rounded-lg p-1.5 flex items-center justify-center flex-shrink-0 shadow-sm border border-neutral-700">
          <Image
            src={imgSrc || "/odoo/icons/green_thumbs-up.svg"}
            alt={industry}
            className="w-full h-full object-contain"
            width={40}
            height={40}
            onError={() => {
              setImgSrc("/odoo/icons/green_thumbs-up.svg");
            }}
          />
        </div>
      );
    }
    return (
      <div className="w-10 h-10 bg-white/5 border border-neutral-700 rounded-lg flex items-center justify-center text-xl shadow-sm">
        🧩
      </div>
    );
  };

  return (
    <div className="w-full max-w-3xl mx-auto px-4 py-12 md:py-20 relative z-10">
      <div className="text-center mb-10">
        <h1 className={`text-6xl md:text-7xl font-bold text-white mb-4 ${caveat.className}`}>
          Get <span className="relative">
            Started
            <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#f1833b]" preserveAspectRatio="none" viewBox="0 0 100 20">
              <path d="M0,10 Q25,0 50,10 T100,10" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
            </svg>
          </span>
        </h1>
        <p className="text-gray-300 font-medium">Free instant access. No credit card required.</p>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Industry Selection Box */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-[#0a0a0a] border border-neutral-700 rounded-lg p-4 mb-8">
          <div className="flex items-center gap-3 mb-3 sm:mb-0">
            {getIndustryIcon()}
            <span className="text-white font-bold">{industry}</span>
          </div>
          <Link href="/odoo">
            <button type="button" className="text-sm text-gray-400 hover:text-white font-medium transition-colors bg-white/5 px-3 py-1.5 rounded border border-neutral-700">
              Change industry selection
            </button>
          </Link>
        </div>

        {submitted ? (
          <div className="text-center py-12 px-6 bg-white/5 border border-neutral-700 rounded-2xl backdrop-blur-md">
            <span className="text-6xl mb-6 block">🚀</span>
            <h3 className="text-2xl font-bold text-white mb-3">Registration Successful!</h3>
            <p className="text-gray-300 mb-6 max-w-md mx-auto">
              Your free 15-day trial environment for <strong className="text-[#f1833b]">{industry}</strong> is being prepared. You will receive an email shortly with your login credentials.
            </p>
            <Link href="/odoo">
              <button className="px-8 py-2.5 bg-[#f1833b] text-white font-bold rounded hover:bg-[#e06b24] transition-colors">
                Back to Odoo Home
              </button>
            </Link>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <FloatingInput id="username" label="First and Last Name" />
            <FloatingInput id="company_name" label="Company Name" />

            <div className="grid md:grid-cols-2 gap-x-6">
              <FloatingInput id="email" type="email" label="Email" />
              <FloatingInput id="phone" type="tel" label="Phone Number" defaultValue="+91" />
            </div>

            <div className="grid md:grid-cols-2 gap-x-6">
              <FloatingSelect
                id="country_id"
                label="Country"
                defaultValue="101"
                options={[
                  { value: "3", label: "Afghanistan" },
                  { value: "14", label: "Australia" },
                  { value: "36", label: "Canada" },
                  { value: "46", label: "China" },
                  { value: "74", label: "France" },
                  { value: "56", label: "Germany" },
                  { value: "101", label: "India" },
                  { value: "106", label: "Italy" },
                  { value: "109", label: "Japan" },
                  { value: "150", label: "Mexico" },
                  { value: "183", label: "Russian Federation" },
                  { value: "239", label: "South Africa" },
                  { value: "67", label: "Spain" },
                  { value: "222", label: "United Kingdom" },
                  { value: "224", label: "United States" }
                ]}
              />
              <FloatingSelect
                id="lang"
                label="Language"
                defaultValue="en_US"
                options={[
                  { value: "ar_001", label: "العربية" },
                  { value: "de_DE", label: "Deutsch" },
                  { value: "en_US", label: "English" },
                  { value: "es_ES", label: "Español" },
                  { value: "fr_FR", label: "Français" },
                  { value: "hi_IN", label: "Hindi" },
                  { value: "it_IT", label: "Italiano" },
                  { value: "ja_JP", label: "日本語" },
                  { value: "zh_CN", label: "简体中文" }
                ]}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-x-6">
              <FloatingSelect
                id="company_size"
                label="Company size"
                defaultValue="1-5"
                options={[
                  { value: "1-5", label: "1 - 5 employees" },
                  { value: "5-20", label: "5 - 20 employees" },
                  { value: "20-50", label: "20 - 50 employees" },
                  { value: "50-250", label: "50 - 250 employees" },
                  { value: "250-over", label: "> 250 employees" }
                ]}
              />
              <FloatingSelect
                id="plan"
                label="Primary Interest"
                defaultValue="plan_to_use"
                options={[
                  { value: "plan_to_use", label: "Use it in my company" },
                  { value: "plan_to_sell", label: "Offer Odoo services to other companies" },
                  { value: "plan_to_test_student", label: "I am a student" },
                  { value: "plan_to_test_teacher", label: "I am a teacher" }
                ]}
              />
            </div>

            {/* Checkbox */}
            <div className="flex items-center rounded-lg border border-neutral-700 bg-[#0a0a0a] mb-6 p-4">
              <input
                type="checkbox"
                id="industry_demo"
                name="industry_demo"
                defaultChecked
                className="w-4 h-4 text-[#f1833b] bg-[#0a0a0a] border-neutral-700 rounded focus:ring-[#f1833b] focus:ring-2 mr-3"
              />
              <label htmlFor="industry_demo" className="text-gray-300 text-sm">
                Load demo data to test the industry's features with sample records.
              </label>
            </div>

            {/* Agreement & Submit */}
            <div className="text-center pt-4">
              <div className="text-xs text-gray-400 mb-6">
                By clicking on <strong className="text-white">Start Now</strong>, you accept our <a href="#" className="underline text-gray-300 hover:text-[#f1833b]">Subscription Agreement</a> and <a href="#" className="underline text-gray-300 hover:text-[#f1833b]">Privacy Policy</a>
              </div>

              <button
                type="submit"
                className="px-10 py-3 bg-[#f1833b] text-white font-bold rounded hover:bg-[#e06b24] transition-colors shadow-md text-lg"
              >
                Start Now
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function StartPage({ slug }) {
  return (
    <div className={`min-h-screen bg-[#0a0a0a8c] text-white overflow-hidden relative ${inter.className}`} style={{ '--font-main': inter.style.fontFamily }}>
      {/* Dynamic Dark Background Elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#f1833b]/5 rounded-full blur-[140px] pointer-events-none -z-0" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-white/5 rounded-full blur-[120px] pointer-events-none -z-0" />

      <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-gray-500 relative z-10">Loading...</div>}>
        <StartForm slug={slug} />
      </Suspense>
    </div>
  );
}
